import { CreditCard, Plus, Trash2, Wallet } from 'lucide-react';

export function PaymentMethodSettings() {
  const paymentMethods = [
    {
      id: '1',
      type: 'Visa',
      last4: '4242',
      expiryMonth: '12',
      expiryYear: '2026',
      isDefault: true,
    },
    {
      id: '2',
      type: 'Mastercard',
      last4: '8888',
      expiryMonth: '09',
      expiryYear: '2025',
      isDefault: false,
    },
  ];

  const getCardIcon = (type: string) => {
    return (
      <div className="w-12 h-8 bg-gradient-to-br from-gray-700 to-gray-900 rounded flex items-center justify-center">
        <span className="text-white text-xs font-bold">{type}</span>
      </div>
    );
  };

  return (
    <div className="max-w-4xl space-y-6">
      <div className="mb-6 flex items-center gap-3">
        <div className="w-12 h-12 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-xl flex items-center justify-center shadow-md">
          <Wallet className="w-6 h-6 text-[#1875FF]" />
        </div>
        <div>
          <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight font-display">Payment Method</h2>
          <p className="text-gray-600">Manage your payment methods and billing information</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-extrabold text-gray-900 font-display">Saved Payment Methods</h3>
          <button className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#1875FF] to-[#05E0D1] text-white text-sm font-medium rounded-lg hover:shadow-lg transition-all">
            <Plus className="w-4 h-4" />
            Add Payment Method
          </button>
        </div>

        <div className="space-y-4">
          {paymentMethods.map((method) => (
            <div
              key={method.id}
              className={`p-4 rounded-lg border-2 transition-all ${
                method.isDefault
                  ? 'border-[#1875FF] bg-gradient-to-r from-[#1875FF]/5 to-[#05E0D1]/5'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {getCardIcon(method.type)}
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">
                        {method.type} ending in {method.last4}
                      </span>
                      {method.isDefault && (
                        <span className="px-2 py-0.5 bg-[#1875FF] text-white text-xs font-medium rounded">
                          Default
                        </span>
                      )}
                    </div>
                    <span className="text-sm text-gray-600">
                      Expires {method.expiryMonth}/{method.expiryYear}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!method.isDefault && (
                    <button className="px-3 py-1.5 text-sm text-[#1875FF] hover:bg-[#1875FF]/5 rounded-lg transition-colors">
                      Set as Default
                    </button>
                  )}
                  <button className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <h3 className="text-lg font-extrabold text-gray-900 font-display mb-4">Billing Address</h3>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Name
              </label>
              <input
                type="text"
                defaultValue="Acme Corporation"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1875FF] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Country
              </label>
              <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1875FF] focus:border-transparent">
                <option>United States</option>
                <option>Canada</option>
                <option>United Kingdom</option>
                <option>Australia</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Street Address
            </label>
            <input
              type="text"
              defaultValue="123 Main Street"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1875FF] focus:border-transparent"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                City
              </label>
              <input
                type="text"
                defaultValue="San Francisco"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1875FF] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                State/Province
              </label>
              <input
                type="text"
                defaultValue="CA"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1875FF] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ZIP/Postal Code
              </label>
              <input
                type="text"
                defaultValue="94102"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1875FF] focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex justify-end pt-4">
            <button className="px-6 py-2 bg-gradient-to-r from-[#1875FF] to-[#05E0D1] text-white font-medium rounded-lg hover:shadow-lg transition-all">
              Save Changes
            </button>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <CreditCard className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-blue-900 mb-1">Secure Payment Processing</h4>
            <p className="text-sm text-blue-800">
              All payment information is encrypted and securely processed. We never store your complete card details.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
